import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import useStyles from "./style";
import {
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  SearchOutlined,
  ChevronLeft,
  ChevronRight,
  ArrowBackOutlined,
} from "@mui/icons-material";
import _ from "lodash";
import moment from "moment-timezone";
import { CUSTOM_CONSTANTS } from "Constants";

import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";

// Services
import DriverApi from "Services/Driver";

const Driverearningslogs = () => {
  const { id: userId } = useParams();
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [row, setRow] = useState([]);
  const [searchText, setSearchText] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const timezone = useSelector((state) => state.timezone.timezone);

  // Get driver earnings logs list api
  const getdriverearningslogs = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        setTotalPages(res?.data?.data?.totalRecords || 1);
        setRow(res?.data?.data?.data || []);
      } else {
        setRow([]);
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to fetch data",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    DriverApi.getdriverEarning({ page, limit: pageSize, userId }).then(onSuccess, onFailure);
  }, [page, pageSize, userId, dispatch]);

  useEffect(() => {
    getdriverearningslogs();
  }, [getdriverearningslogs]);

  const calculateSno = (pageIndex, pSize, index) => {
    return (pageIndex - 1) * pSize + index + 1;
  };

  const dataRows = useMemo(() => {
    let filtered = row || [];
    if (searchText.trim()) {
      const query = searchText.toLowerCase();
      filtered = filtered.filter(
        (data) =>
          data?.userdetails?.user_name?.toLowerCase().includes(query) ||
          data?.userdetails?.email?.toLowerCase().includes(query) ||
          data?.bookingId?.toString().toLowerCase().includes(query) ||
          data?.earningStatus?.toLowerCase().includes(query)
      );
    }
    return _.map(filtered, (data, index) => ({
      sno: calculateSno(page, pageSize, index),
      username: data?.userdetails?.user_name || "-",
      email: data?.userdetails?.email || "-",
      booking_id: data?.bookingId || "-",
      earnings: data?.earnings || 0,
      earningStatus: data?.earningStatus || "-",
      createdAt: data?.createdAt
        ? moment(data?.createdAt).tz(timezone).format("YYYY-MM-DD hh:mm A")
        : "-",
      row: data,
    }));
  }, [row, page, pageSize, searchText, timezone]);

  // Pagination
  const totalPagesCount = Math.ceil(totalPages / pageSize) || 1;

  const renderPagination = () => {
    let pages = [];
    const internalPage = page - 1;
    if (totalPagesCount <= 5) {
      for (let i = 0; i < totalPagesCount; i++) pages.push(i);
    } else {
      if (internalPage < 3) {
        pages = [0, 1, 2, "...", totalPagesCount - 1];
      } else if (internalPage > totalPagesCount - 4) {
        pages = [0, "...", totalPagesCount - 3, totalPagesCount - 2, totalPagesCount - 1];
      } else {
        pages = [0, "...", internalPage - 1, internalPage, internalPage + 1, "...", totalPagesCount - 1];
      }
    }

    return pages.map((item, index) => {
      if (item === "...") {
        return (
          <span key={index} className={classes.pageDots}>
            ...
          </span>
        );
      }
      return (
        <Button
          key={item}
          className={internalPage === item ? classes.pageButtonActive : classes.pageButton}
          onClick={() => setPage(item + 1)}
        >
          {item + 1}
        </Button>
      );
    });
  };

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.headerContainer}>
        <Box className={classes.headerLeft}>
          <Button
            className={classes.backButton}
            onClick={() => navigate(-1)}
            startIcon={<ArrowBackOutlined />}
          >
            Back
          </Button>
          <Typography className={classes.pageTitle}>Driver Earning Logs</Typography>
        </Box>
        <Typography className={classes.breadcrumb}>
          Admin / <span>Driver Earning Logs</span>
        </Typography>
      </Box>

      {/* Action Row */}
      <Box className={classes.actionRow}>
        {/* Search */}
        <Box className={classes.searchWrapper}>
          <SearchOutlined className={classes.searchIcon} />
          <input
            type="text"
            placeholder="Search earning logs..."
            className={classes.searchInput}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPage(1);
            }}
          />
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={0} className={classes.tableWrapper}>
        <Table>
          <TableHead>
            <TableRow className={classes.tableHeaderRow}>
              <TableCell className={classes.tableHeaderCell}>S.No</TableCell>
              <TableCell className={classes.tableHeaderCell}>Name</TableCell>
              <TableCell className={classes.tableHeaderCell}>Email</TableCell>
              <TableCell className={classes.tableHeaderCell}>Booking ID</TableCell>
              <TableCell className={classes.tableHeaderCell}>Earning</TableCell>
              <TableCell className={classes.tableHeaderCell}>Earning Status</TableCell>
              <TableCell className={classes.tableHeaderCell}>Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dataRows.map((rowItem) => (
              <TableRow key={rowItem.sno} className={classes.tableRow}>
                <TableCell className={classes.tableCell}>{rowItem.sno}</TableCell>
                <TableCell className={`${classes.tableCell} ${classes.boldText}`}>
                  {rowItem.username}
                </TableCell>
                <TableCell className={classes.tableCell}>{rowItem.email}</TableCell>
                <TableCell className={classes.tableCell}>{rowItem.booking_id}</TableCell>
                <TableCell className={classes.tableCell}>
                  {rowItem.earnings} {CUSTOM_CONSTANTS.BASE_CURRENCY}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <Box
                    sx={{
                      display: "inline-block",
                      padding: "4px 12px",
                      borderRadius: "100px",
                      fontSize: "12px",
                      fontWeight: "600",
                      textTransform: "capitalize",
                      backgroundColor: "#e0f2fe",
                      color: "#0369a1",
                    }}
                  >
                    {rowItem.earningStatus}
                  </Box>
                </TableCell>
                <TableCell className={classes.tableCell}>{rowItem.createdAt}</TableCell>
              </TableRow>
            ))}
            {dataRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} style={{ textAlign: "center", padding: "40px" }}>
                  <Typography variant="body2" color="textSecondary">
                    No earning logs found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <Box className={classes.paginationContainer}>
          <Typography className={classes.paginationText}>
            Showing {(page - 1) * pageSize + (dataRows.length > 0 ? 1 : 0)} to{" "}
            {Math.min(page * pageSize, totalPages)} of {totalPages} results
          </Typography>

          <Box className={classes.paginationControls}>
            <ChevronLeft
              className={`${classes.pageArrow} ${page === 1 ? "disabled" : ""}`}
              onClick={() => page > 1 && setPage(page - 1)}
            />
            {renderPagination()}
            <ChevronRight
              className={`${classes.pageArrow} ${page === totalPagesCount ? "disabled" : ""}`}
              onClick={() => page < totalPagesCount && setPage(page + 1)}
            />
          </Box>
        </Box>
      </TableContainer>
    </Box>
  );
};

export default Driverearningslogs;
